import React, { memo, Fragment } from 'react';
import {
  SearchFilterMultiSelect,
  SearchFilterRange,
  SearchFilterSingleSelect,
  SearchFiltersSection,
  Stack,
  Divider,
  Typography
} from 'investira.react.components';
import { ATIVO_TIPO, FUNDO_DISTRIBUICAO, PUBLICO_ALVO } from '@investirapri/financa.js/lib/enums';
import FUNDO_SITUACAO from '../../../enums/FUNDO_SITUACAO';
import BENCHMARKS from '../../../enums/BENCHMARKS';
import { ATIVOS_FILTER_CONTROL, getAtivosFilterConfig } from '../../../utils/ativosFilterRegistry';

/** Ordena as opções de enum apresentadas nos filtros do diálogo. */
function pvOrderOptions(pValues, pLabel) {
  return Object.values(pValues).sort((pLeft, pRight) =>
    String(pLeft[pLabel] || '').localeCompare(String(pRight[pLabel] || ''))
  );
}

/** Resolve o identificador compatível com os formatos de enum do app. */
function pvGetAtivoTipoId(pOption = {}) {
  return pOption.id || pOption.ativo_tipo_id || '';
}

/** Converte o estado vazio para o contrato anulável do filtro numérico. */
function pvToNumber(pValue) {
  if (pValue === '' || pValue === null || typeof pValue === 'undefined') {
    return null;
  }

  const xValue = Number(pValue);

  return Number.isFinite(xValue) ? xValue : null;
}

const wBooleanOptions = [
  { id: 'todos', label: 'Todos', value: '', neutral: true },
  { id: 'sim', label: 'Sim', value: 'true' },
  { id: 'nao', label: 'Não', value: 'false' }
];

const wOpenClosedOptions = [
  { id: 'todos', label: 'Todos', value: '', neutral: true },
  { id: 'aberto', label: 'Aberto', value: 'false' },
  { id: 'fechado', label: 'Fechado', value: 'true' }
];

const wAtivoTipoOptions = pvOrderOptions(ATIVO_TIPO, 'descricao')
  .filter(pOption => pOption.mercado === 'FND')
  .filter(
    pOption =>
      !['BDRETFRV', 'BDRETFRF', 'BDRETFCM', 'CTCB', 'EMPETF', 'EMPCT', 'CTUM', 'CTSM'].includes(
        pOption.id
      )
  )
  .map(pOption => ({
    id: pvGetAtivoTipoId(pOption),
    label: `${pOption.sigla} - ${pOption.descricao}`,
    value: pvGetAtivoTipoId(pOption)
  }));

const wSituacaoOptions = pvOrderOptions(FUNDO_SITUACAO, 'descricao')
  .filter(pOption => pOption.value !== FUNDO_SITUACAO.EM_FUNCIONAMENTO_NORMAL.value)
  .map(pOption => ({
    id: pOption.id,
    label: pOption.descricao,
    value: pOption.value
  }));

const wBenchmarkOptions = pvOrderOptions(BENCHMARKS, 'index').map(pOption => ({
  id: pOption.indicador_id,
  label: pOption.indicador_sigla,
  value: pOption.indicador_id
}));

const wPublicoAlvoOptions = pvOrderOptions(PUBLICO_ALVO, 'descricao').map(pOption => ({
  id: pOption.id,
  label: pOption.descricao,
  value: pOption.id
}));

const wDistribuicaoOptions = pvOrderOptions(FUNDO_DISTRIBUICAO, 'descricao').map(pOption => ({
  id: pOption.id,
  label: pOption.descricao,
  value: pOption.id
}));

const wSectionFilterNames = {
  identificacao: ['ativo_tipo_id'],
  caracteristicas: [
    'situacao',
    'exclusivo',
    'fechado',
    'longo',
    'cotas',
    'condominio_fechado',
    'responsabilidade_limitada',
    'qualificado',
    'profissional',
    'publico_alvo',
    'distribuicao',
    'previdenciario',
    'estruturado',
    'esg'
  ],
  custosLiquidez: ['taxa_adm', 'aplicacao_min', 'resgate_carencia'],
  desempenhoRisco: [
    'benchmark_indicador_id',
    'retorno',
    'perc_dias_acima_benchmark',
    'perc_meses_acima_benchmark',
    'sharpe',
    'tracking_error',
    'volatilidade'
  ],
  patrimonioEscala: ['pl', 'cotistas', 'gestor_pl', 'adm_pl']
};

const wFilterSections = [
  {
    id: 'classificacao',
    title: 'Tipo / Estratégia',
    fields: ['ativo_tipo_id', 'top_setor_id']
  },
  {
    id: 'caracteristicas',
    title: 'Características',
    fields: [
      // Reavaliar futuramente se a adaptação à Resolução CVM 175 deve voltar à interface.
      // 'cvm_175',
      'situacao',
      'exclusivo',
      'fechado',
      'longo',
      'cotas',
      'condominio_fechado',
      'responsabilidade_limitada',
      'qualificado',
      'profissional',
      'publico_alvo',
      'distribuicao',
      'previdenciario',
      'estruturado',
      'esg'
    ]
  },
  {
    id: 'custos-liquidez',
    title: 'Custos e condições',
    fields: ['taxa_adm', 'aplicacao_min', 'resgate_carencia']
  },
  {
    id: 'desempenho-risco',
    title: 'Desempenho e risco',
    fields: [
      'benchmark_indicador_id',
      'retorno',
      'perc_dias_acima_benchmark',
      'perc_meses_acima_benchmark',
      'sharpe',
      'tracking_error',
      'volatilidade'
    ]
  },
  {
    id: 'patrimonio-escala',
    title: 'Patrimônio e cotistas',
    groups: [
      { id: 'fundo', title: 'Fundo', fields: ['pl', 'cotistas'] },
      { id: 'gestor', title: 'Gestor', fields: ['gestor_pl', 'gestor_cotistas'] },
      { id: 'administrador', title: 'Administrador', fields: ['adm_pl', 'adm_cotistas'] }
    ]
  }
];

const pvNoop = () => {};

/** Conta os campos ativos de uma seção sem perder valores falsy válidos. */
function pvCountActiveFilters(pFilters, pNames) {
  return pNames.filter(pName => {
    const xValue = pFilters[pName];

    return Array.isArray(xValue) ? xValue.length > 0 : xValue !== '' && xValue != null;
  }).length;
}

/** Renderiza os filtros de fundos e devolve o próximo estado ao diálogo. */
const ComparadorFundosFilters = memo(pProps => {
  const { filters, appliedFilters, onChange, onValidityChange = pvNoop } = pProps;

  const xConfig = getAtivosFilterConfig('FND');
  const xFields = xConfig?.fields || [];

  const xAppliedValues = appliedFilters;
  const xAppliedValuesKey = JSON.stringify(xAppliedValues);

  const xIdentificacaoActiveFiltersCount = pvCountActiveFilters(
    appliedFilters,
    wSectionFilterNames.identificacao
  );
  const xCaracteristicasActiveFiltersCount = pvCountActiveFilters(
    appliedFilters,
    wSectionFilterNames.caracteristicas
  );
  const xCustosLiquidezActiveFiltersCount = pvCountActiveFilters(
    appliedFilters,
    wSectionFilterNames.custosLiquidez
  );
  const xDesempenhoRiscoActiveFiltersCount = pvCountActiveFilters(
    appliedFilters,
    wSectionFilterNames.desempenhoRisco
  );
  const xPatrimonioEscalaActiveFiltersCount = pvCountActiveFilters(
    appliedFilters,
    wSectionFilterNames.patrimonioEscala
  );

  /** Atualiza somente o campo informado no estado controlado do diálogo. */
  function pvChangeFilter(pFilter, pValue) {
    onChange({ ...filters, [pFilter.name]: pValue });
  }

  /** Converte o valor anulável da faixa para o estado vazio do diálogo. */
  function pvChangeNumberFilter(pName, pValue) {
    pvChangeFilter(pName, pValue ?? '');
  }

  /** Mantém somente o limite que pertence ao campo snake_case do app. */
  function pvChangeRange(pName, pLimit, pValue) {
    pvChangeNumberFilter(pName, pValue[pLimit]);
  }

  /** Encaminha a validade da faixa ao consumidor pelo nome do campo. */
  function pvChangeValidity(pName, pValid) {
    onValidityChange(pName, pValid);
  }

  /** Conta os campos que possuem valor efetivamente aplicado na URL. */
  function pvCountAppliedFilters(pValues, pFields) {
    return pFields.filter(pFilter => {
      const xValue = pValues[pFilter.name];

      return Array.isArray(xValue) ? xValue.length > 0 : xValue !== '' && xValue != null;
    }).length;
  }

  /** Renderiza o controle correspondente ao contrato do registry. */
  function pvRenderFilter(pFilter) {
    const xId = `ativos-${pFilter.name}`;
    const xValue = filters[pFilter.name];

    if (pFilter.control === ATIVOS_FILTER_CONTROL.MULTI_SELECT) {
      return (
        <SearchFilterMultiSelect
          id={xId}
          label={pFilter.label}
          value={Array.isArray(xValue) ? xValue : []}
          options={pFilter.options}
          visibleOptionCount={pFilter.name === 'ativo_tipo_id' ? 6 : undefined}
          optionsDialogTitle={pFilter.label}
          onChange={pValue => pvChangeFilter(pFilter, pValue)}
        />
      );
    }

    if (pFilter.control === ATIVOS_FILTER_CONTROL.SINGLE_SELECT) {
      return (
        <SearchFilterSingleSelect
          id={xId}
          label={pFilter.label}
          value={xValue}
          options={pFilter.options}
          onChange={pValue => pvChangeFilter(pFilter, pValue)}
        />
      );
    }

    return (
      <SearchFilterRange
        id={xId}
        label={pFilter.label}
        mode={pFilter.direction === 'min' ? 'min-only' : 'max-only'}
        showSlider={Boolean(pFilter.showSlider)}
        value={{
          min: pFilter.direction === 'min' ? pvToNumber(xValue) : null,
          max: pFilter.direction === 'max' ? pvToNumber(xValue) : null
        }}
        min={pFilter.minimum}
        max={pFilter.maximum}
        step={pFilter.step ?? (pFilter.decimalScale === 0 ? 1 : 0.01)}
        sliderScale={pFilter.sliderScale}
        currency={pFilter.currency}
        prefix={pFilter.prefix}
        suffix={pFilter.suffix}
        onChange={pValue =>
          pvChangeFilter(pFilter, pValue[pFilter.direction === 'min' ? 'min' : 'max'])
        }
        onValidityChange={pValid => pvChangeValidity(pFilter.name, pValid)}
      />
    );
  }

  /** Renderiza uma lista de filtros com divisores internos. */
  function pvRenderFilterList(pFilters) {
    return pFilters.map((pFilter, pIndex) => (
      <Fragment key={pFilter.name}>
        {pIndex > 0 && <Divider />}
        {pvRenderFilter(pFilter)}
      </Fragment>
    ));
  }

  return (
    <Stack sx={{ minWidth: 0, px: 0, pb: 1 }}>
      {wFilterSections.map(pSection => {
        const xSectionGroups = (pSection.groups || [pSection])
          .map(pGroup => ({
            ...pGroup,
            filters: pGroup.fields
              .map(pName => xFields.find(pFilter => pFilter.name === pName))
              .filter(Boolean)
          }))
          .filter(pGroup => pGroup.filters.length);
        const xSectionFields = xSectionGroups.reduce(
          (pFields, pGroup) => [...pFields, ...pGroup.filters],
          []
        );

        if (!xSectionFields.length) {
          return null;
        }

        const xSectionAppliedCount = pvCountAppliedFilters(xAppliedValues, xSectionFields);

        return (
          <SearchFiltersSection
            key={pSection.id}
            id={`ativos-${pSection.id}`}
            activeFiltersCount={xSectionAppliedCount}
            title={pSection.title}>
            {pSection.groups
              ? xSectionGroups.map((pGroup, pIndex) => {
                  const xGroupTitleId = `ativos-${pSection.id}-${pGroup.id}-title`;

                  return (
                    <Fragment key={pGroup.id}>
                      {pIndex > 0 && <Divider />}
                      <Stack
                        aria-labelledby={xGroupTitleId}
                        role="group"
                        sx={{ gap: 1, minWidth: 0 }}>
                        <Typography
                          id={xGroupTitleId}
                          color="text.secondary"
                          fontWeight={600}
                          variant="subtitle2">
                          {pGroup.title}
                        </Typography>
                        {pvRenderFilterList(pGroup.filters)}
                      </Stack>
                    </Fragment>
                  );
                })
              : pvRenderFilterList(xSectionFields)}
          </SearchFiltersSection>
        );
      })}
    </Stack>
  );
});

export default ComparadorFundosFilters;
